import { Config } from "@oclif/core/config"
import { CommandError, Input, ParserOutput } from "@oclif/core/interfaces"
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { ArgOutput, FlagOutput } from "@oclif/core/lib/interfaces/parser.js"
import * as Sentry from "@sentry/node"
import { isAxiosError } from "axios"

import { BaseCommand } from "./base-command.js"
import { NotAuthorizedError } from "./not-authorized.error.js"
import { CLIError } from "@oclif/core/errors"

export abstract class SentryCommand<T extends object> extends BaseCommand<T> {
  protected constructor(argv: string[], config: Config) {
    super(argv, config)
  }

  async catch(error: CommandError & { oclif?: { exit: number } }) {
    if (!(error instanceof CLIError)) {
      // Invocations of `this.error(...)` from command classes will already be logged, no need to repeat
      this.logToStderr(error.message)
    }

    if (error.oclif) throw error
    if (error instanceof NotAuthorizedError) throw error

    if (isAxiosError(error)) {
      const captureMetadata = {
        code: error.code,
        message: error.message,
        response: {
          status: error.response?.status,
          data: error.response?.data,
        },
      }

      Sentry.captureMessage(`Axios error metadata: ${JSON.stringify(captureMetadata)}`)
    }

    Sentry.captureException(error)
    this.logToStderr(`Something went wrong. Please try again later.`)
  }

  async finally() {}

  public async parse<F extends FlagOutput, B extends FlagOutput, A extends ArgOutput>(
    options?: Input<F, B, A>,
    argv?: string[],
  ): Promise<ParserOutput<F, B, A>> {
    Sentry.addBreadcrumb({
      category: "command",
      data: {
        commandName: this.id,
      },
      level: "debug",
      message: "Starting command parse",
    })

    const parsed = await super.parse(options, argv)
    const span = Sentry.getActiveSpan()
    if (span) {
      span.setAttributes({
        args: Object.keys(parsed.args),
        designSystemId: parsed.flags.designSystemId,
        flags: Object.keys(parsed.flags),
        workspaceId: parsed.flags.workspaceId,
      })
    }

    Sentry.addBreadcrumb({
      category: "command",
      data: {
        result: "success",
      },
      level: "debug",
      message: "Command parse completed",
    })

    return parsed
  }
}
